import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, Sparkles } from "lucide-react";
import { api } from "../api/client";

/**
 * /checkout/success?session_id=cs_…
 *
 * Stripe redirects here after a successful Checkout. We poll
 * /api/stripe/checkout-status/:id every 2 s up to ~30 s, only treating the
 * payment as confirmed when the backend says `payment_status === "paid"`.
 *
 * Trust source: BACKEND ONLY. We never grant credits or access from this
 * page — that's done exclusively by the webhook handler.
 */
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

export default function CheckoutSuccess({ isAuthed }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("polling"); // polling | confirmed | timeout | error
  const [info, setInfo] = useState(null);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }
    let cancelled = false;
    let timer = null;

    // Clear any pending-downsell hint — the user did make it through to
    // the success URL so they almost certainly paid.
    try { window.sessionStorage.removeItem("stripe_pending_downsell"); } catch (_) { /* ignore */ }

    async function poll() {
      if (cancelled) return;
      try {
        const r = await api.get(`/stripe/checkout-status/${encodeURIComponent(sessionId)}`);
        const data = r.data || {};
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("confirmed");
          return;
        }
      } catch {
        // fall through — keep polling within timeout
      }
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    }
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const meta = info?.metadata || {};
  const paymentType = meta.payment_type;
  const isCredits = paymentType === "credits";
  const isSubscription = paymentType === "subscription";

  return (
    <div className="checkout-screen">
      <div className="checkout-card" data-testid="checkout-success-card">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="checkout-spinner" />
            <h1 className="checkout-title">Confirming your payment…</h1>
            <p className="checkout-subtitle">
              We're double-checking with Stripe. This usually takes just a few seconds.
            </p>
          </>
        )}

        {status === "confirmed" && (
          <>
            <CheckCircle2 size={56} className="checkout-success-icon" />
            <h1 className="checkout-title">
              {isCredits ? "Credits added! " : isSubscription ? "You're in! " : "Payment confirmed!"}
              <Sparkles size={20} style={{ marginLeft: 6, color: "#10b981" }} />
            </h1>
            <p className="checkout-subtitle">
              {isCredits && (
                <>Your <strong>{meta.credits_amount} credits</strong> are now in your account, ready to use.</>
              )}
              {isSubscription && meta.offer_type && meta.offer_type !== "regular" && (
                <>Your 3-day trial is active. We'll auto-charge the regular {meta.billing_period || "subscription"} price after that — cancel anytime.</>
              )}
              {isSubscription && meta.offer_type === "regular" && (
                <>Your {meta.billing_period || "subscription"} plan is active. Time to make some music.</>
              )}
              {!paymentType && (<>Your purchase has been recorded. Redirecting…</>)}
            </p>
            <div className="checkout-actions">
              <button
                className="v2-btn-primary v2-btn-lg"
                onClick={() => navigate(isAuthed ? "/app" : "/")}
                data-testid="checkout-success-continue"
              >
                Continue to {isAuthed ? "app" : "home"}
              </button>
            </div>
          </>
        )}

        {status === "timeout" && (
          <>
            <AlertCircle size={48} className="checkout-warn-icon" />
            <h1 className="checkout-title">Still confirming…</h1>
            <p className="checkout-subtitle">
              Your payment was likely successful, but Stripe is taking longer than usual to confirm.
              Please check your account in a minute. If credits aren't visible, contact{" "}
              <a href="mailto:support@music-producer.ai">support@music-producer.ai</a>.
            </p>
            <div className="checkout-actions">
              <button
                className="v2-btn-primary v2-btn-lg"
                onClick={() => navigate(isAuthed ? "/app" : "/")}
                data-testid="checkout-success-continue"
              >
                Continue
              </button>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle size={48} className="checkout-warn-icon" />
            <h1 className="checkout-title">Missing session ID</h1>
            <p className="checkout-subtitle">
              We couldn't find a payment session in the URL. If you completed a payment, your account will update shortly.
            </p>
            <div className="checkout-actions">
              <button
                className="v2-btn-primary v2-btn-lg"
                onClick={() => navigate(isAuthed ? "/app" : "/")}
              >
                Continue
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
